/**
 * «Сообщить» (БЗ-21, БЗ-22, БЗ-28): что панель прикладывает к обращению сама — снимок видимой части страницы с
 * пометками, путь страницы, проект, сведения о браузере и последние ошибки страницы. Снимок делает библиотека
 * modern-screenshot (MIT): отдельный файл, загружается при первом открытии панели, а не со всем порталом.
 */

/** Узлы панели «Сообщить», её затемнения и кнопок помечаются этим атрибутом и в снимок не попадают */
export const REPORT_UI_ATTR = 'data-report-ui';

/* ───────────── Ошибки страницы: последние 20 за 30 минут ───────────── */

interface PageError {
  at: number;
  text: string;
}

const ERRORS_KEEP = 20;
const ERRORS_WINDOW_MS = 30 * 60_000;
const pageErrors: PageError[] = [];
let listening = false;

function pushError(text: string, at = Date.now()) {
  pageErrors.push({ at, text: text.replace(/\s+/g, ' ').trim().slice(0, 300) });
  if (pageErrors.length > ERRORS_KEEP) pageErrors.shift();
}

/** Ловить ошибки страницы с первой загрузки: к обращению об ошибке приложится то, что было до открытия панели */
export function listenPageErrors() {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  window.addEventListener('error', (e) => {
    const where = e.filename ? ` @${e.filename.split('/').pop()}:${e.lineno}` : '';
    pushError(`${e.message || 'Ошибка'}${where}`);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason as { message?: unknown } | undefined;
    pushError(`Обещание без обработки: ${typeof r?.message === 'string' ? r.message : String(e.reason)}`);
  });
}

/** Ошибки страницы за последние 30 минут: «10:42:05 текст» */
export function recentErrors(now = Date.now(), list: readonly PageError[] = pageErrors): string[] {
  const hhmmss = (t: number) => new Date(t).toTimeString().slice(0, 8);
  return list.filter((e) => now - e.at <= ERRORS_WINDOW_MS).map((e) => `${hhmmss(e.at)} ${e.text}`);
}

/** Только для тестов: записать ошибку с заданным временем */
export const pushErrorForTest = pushError;

/* ───────────── Тема, страница, проект ───────────── */

/** Тема обращения — первая непустая строка текста, не длиннее 120 знаков */
export function subjectFrom(text: string): string {
  const line =
    text
      .split('\n')
      .map((x) => x.trim())
      .find(Boolean) ?? '';
  return line.length > 120 ? `${line.slice(0, 117).trimEnd()}…` : line;
}

const PATH_RE = /^\/[A-Za-z0-9/_\-.?=&%#]*$/;

/**
 * Адрес страницы для обращения: путь и параметры, как в адресной строке. Длиннее 300 знаков или с чужими знаками —
 * только путь; путь тоже не прошёл — null (обращение уходит без адреса, а не отказом сервера)
 */
export function reportPagePath(pathname: string, search: string): string | null {
  const full = `${pathname}${search}`;
  if (full.length <= 300 && PATH_RE.test(full)) return full;
  const bare = pathname.slice(0, 300);
  return PATH_RE.test(bare) ? bare : null;
}

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const PROJECT_RE = new RegExp(`^/projects/(${UUID})(?:/|$)`, 'i');

/** Проект страницы: обращение со страницы проекта связывается с ним (ссылка в обращении и отбор по проекту) */
export function projectIdFrom(pathname: string): string | null {
  return PROJECT_RE.exec(pathname)?.[1]?.toLowerCase() ?? null;
}

/* ───────────── Сведения о браузере ───────────── */

export function browserName(ua: string): string {
  const v = (re: RegExp) => re.exec(ua)?.[1];
  if (/YaBrowser\/(\d+)/.test(ua)) return `Яндекс Браузер ${v(/YaBrowser\/(\d+)/)}`;
  if (/Edg\/(\d+)/.test(ua)) return `Edge ${v(/Edg\/(\d+)/)}`;
  if (/OPR\/(\d+)/.test(ua)) return `Opera ${v(/OPR\/(\d+)/)}`;
  if (/Firefox\/(\d+)/.test(ua)) return `Firefox ${v(/Firefox\/(\d+)/)}`;
  if (/CriOS\/(\d+)/.test(ua)) return `Chrome ${v(/CriOS\/(\d+)/)}`;
  if (/Chrome\/(\d+)/.test(ua)) return `Chrome ${v(/Chrome\/(\d+)/)}`;
  if (/Version\/(\d+)[.\d]* .*Safari/.test(ua)) return `Safari ${v(/Version\/(\d+)/)}`;
  return 'браузер не распознан';
}

export function systemName(ua: string): string {
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Mac OS X|Macintosh/.test(ua)) return 'macOS';
  if (/Linux/.test(ua)) return 'Linux';
  return 'система не распознана';
}

export interface DeviceInfo {
  ua: string;
  width: number;
  height: number;
  screenWidth: number;
  screenHeight: number;
  pixelRatio: number;
  theme: string;
  timeZone: string;
  online: boolean;
}

/** Одна строка для поддержки: «Chrome 141, Windows · окно 1440×900 · экран 1920×1080 ×1,25 · тема светлая · …» */
export function deviceLine(d: DeviceInfo): string {
  const ratio =
    Number.isFinite(d.pixelRatio) && d.pixelRatio !== 1 ? ` ×${String(d.pixelRatio).replace('.', ',')}` : '';
  const parts = [
    `${browserName(d.ua)}, ${systemName(d.ua)}`,
    `окно ${d.width}×${d.height}`,
    `экран ${d.screenWidth}×${d.screenHeight}${ratio}`,
    `тема ${d.theme === 'dark' ? 'тёмная' : 'светлая'}`,
    d.timeZone ? `пояс ${d.timeZone}` : '',
    d.online ? '' : 'нет связи',
  ].filter(Boolean);
  return parts.join(' · ').slice(0, 300);
}

export function currentDevice(): DeviceInfo {
  let timeZone = '';
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    // часовой пояс браузер не сообщил
  }
  return {
    ua: navigator.userAgent,
    width: window.innerWidth,
    height: window.innerHeight,
    screenWidth: window.screen?.width ?? 0,
    screenHeight: window.screen?.height ?? 0,
    pixelRatio: Math.round((window.devicePixelRatio || 1) * 100) / 100,
    theme: document.documentElement.dataset.theme ?? 'light',
    timeZone,
    online: navigator.onLine,
  };
}

/** Текстовый файл для поддержки, если на странице были ошибки: браузер целиком, адрес и сами ошибки */
export function errorsReport(d: DeviceInfo, page: string, errors: string[]): string {
  return [
    'Сведения о странице для поддержки портала',
    `Страница: ${page}`,
    `Браузер: ${d.ua}`,
    `Окно: ${d.width}×${d.height}, экран: ${d.screenWidth}×${d.screenHeight}, масштаб: ${d.pixelRatio}`,
    '',
    `Ошибки страницы за 30 минут (${errors.length}):`,
    ...errors,
    '',
  ].join('\n');
}

/* ───────────── Суммы на странице ───────────── */

const MONEY_RE = /\d[\d\s  .,]*\s?(?:тыс\.|млн|млрд)?\s?(?:₸|₽|€|\$|KZT|RUB|USD|EUR)/;

/**
 * На странице видны суммы: снимок по умолчанию выключен. Обращение читает поддержка, предложение — все сотрудники,
 * а у них уровень доступа к деньгам может быть ниже, чем у автора (ДС-04)
 */
export function textShowsMoney(text: string): boolean {
  return MONEY_RE.test(text);
}

/* ───────────── Снимок экрана ───────────── */

function skipReportUi(node: Node): boolean {
  if (node.nodeType !== 1) return true;
  const el = node as Element;
  // Своя панель, затемнение, круглая кнопка и уведомления; рамка-песочница библиотеки снимков
  return !el.hasAttribute(REPORT_UI_ATTR) && !el.id?.startsWith('__SANDBOX__');
}

/**
 * Снимок видимой части страницы, как её видит человек: страница снимается целиком и обрезается по окну. Масштаб
 * не выше 1,5 — снимок не весит десятки мегабайт на телефонах с плотным экраном.
 */
export async function captureViewport(): Promise<HTMLCanvasElement> {
  const sx = window.scrollX;
  const sy = window.scrollY;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const scale = Math.min(window.devicePixelRatio || 1, 1.5);
  const { domToCanvas } = await import('modern-screenshot');
  const body = document.body;
  const bg = getComputedStyle(body).backgroundColor;
  const full = await domToCanvas(body, {
    scale,
    // Библиотека не переносит поля (margin) снимаемого узла, и body получил бы поля браузера по умолчанию — 8 px
    style: { margin: '0' },
    filter: skipReportUi,
    timeout: 8000,
    maximumCanvasSize: 16384,
    backgroundColor: bg && bg !== 'rgba(0, 0, 0, 0)' ? bg : '#ffffff',
  });
  // Библиотека снимает body по его рамке: во столько раз снимок крупнее страницы
  const k = full.width / Math.max(body.getBoundingClientRect().width, 1);
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(vw * k));
  out.height = Math.max(1, Math.round(vh * k));
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('Браузер не дал нарисовать снимок');
  ctx.fillStyle = bg && bg !== 'rgba(0, 0, 0, 0)' ? bg : '#ffffff';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(
    full,
    Math.round(sx * k),
    Math.round(sy * k),
    out.width,
    out.height,
    0,
    0,
    out.width,
    out.height,
  );
  return out;
}

/** Картинка из буфера обмена или файла — как снимок (вставить снимок: Ctrl+V) */
export function canvasFromImage(img: HTMLImageElement): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  c.getContext('2d')?.drawImage(img, 0, 0);
  return c;
}

export function canvasToJpeg(canvas: HTMLCanvasElement, quality = 0.85): Promise<Blob | null> {
  return new Promise((resolve) => {
    if (!canvas.toBlob) return resolve(null);
    canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
  });
}

/* ───────────── Пометки на снимке ───────────── */

export type MarkTool = 'rect' | 'arrow' | 'text' | 'hide';

/** Пометка в точках исходного снимка: рамка, стрелка, подпись или закрашенный прямоугольник «Скрыть данные» */
export interface Mark {
  t: MarkTool;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Текст подписи */
  s?: string;
}

/**
 * Пометка после того, как человек отпустил мышь или палец: прямоугольник — от левого верхнего угла, стрелка — как
 * провели; случайное касание (сдвиг меньше 6 точек) — не пометка
 */
export function normalizeMark(m: Mark): Mark | null {
  if (![m.x, m.y, m.w, m.h].every(Number.isFinite)) return null;
  if (m.t === 'text') return m.s?.trim() ? { ...m, w: 0, h: 0, s: m.s.trim().slice(0, 120) } : null;
  if (Math.abs(m.w) + Math.abs(m.h) <= 6) return null;
  if (m.t === 'arrow') return { ...m };
  const x = m.w < 0 ? m.x + m.w : m.x;
  const y = m.h < 0 ? m.y + m.h : m.y;
  return { ...m, x, y, w: Math.abs(m.w), h: Math.abs(m.h) };
}

const MARK_RED = '#d92d20';
const HIDE_FILL = '#2b2f33';

/** Нарисовать снимок и пометки на холсте того же размера */
export function drawMarks(
  ctx: CanvasRenderingContext2D,
  base: HTMLCanvasElement,
  marks: readonly Mark[],
): void {
  ctx.drawImage(base, 0, 0);
  const lw = Math.max(3, base.width / 400);
  for (const m of marks) {
    if (m.t === 'hide') {
      ctx.fillStyle = HIDE_FILL;
      ctx.fillRect(m.x, m.y, m.w, m.h);
      continue;
    }
    ctx.strokeStyle = MARK_RED;
    ctx.fillStyle = MARK_RED;
    ctx.lineWidth = lw;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (m.t === 'rect') ctx.strokeRect(m.x, m.y, m.w, m.h);
    if (m.t === 'arrow') {
      const x2 = m.x + m.w;
      const y2 = m.y + m.h;
      const a = Math.atan2(m.h, m.w);
      const len = lw * 5;
      ctx.beginPath();
      ctx.moveTo(m.x, m.y);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - len * Math.cos(a - 0.45), y2 - len * Math.sin(a - 0.45));
      ctx.lineTo(x2 - len * Math.cos(a + 0.45), y2 - len * Math.sin(a + 0.45));
      ctx.closePath();
      ctx.fill();
    }
    if (m.t === 'text' && m.s) {
      const fs = Math.max(16, base.width / 70);
      ctx.font = `600 ${fs}px Geologica, Arial, sans-serif`;
      const tw = ctx.measureText(m.s).width;
      ctx.fillRect(m.x, m.y - fs * 1.3, tw + fs * 0.8, fs * 1.6);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(m.s, m.x + fs * 0.4, m.y - fs * 0.05);
    }
  }
}

/** Снимок с пометками — новый холст; исходный не меняется, пометки можно переделать */
export function renderMarked(base: HTMLCanvasElement, marks: readonly Mark[]): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = base.width;
  c.height = base.height;
  const ctx = c.getContext('2d');
  if (ctx) drawMarks(ctx, base, marks);
  return c;
}
